
import React, { useState } from "react";
import {
  alpha,
  Box,
  ButtonBase,
  Container,
  Modal,
  Typography,
} from "@mui/material";
import { IoChevronBack, IoChevronUp, IoClose } from "react-icons/io5";
import DefaultLayout from "Containers/Layout/DefaultLayout";
import Title from "Pages/Components/Title";
import JGIcon from "Pages/Components/JGIcon";
import DevicesShowcase from "Pages/Components/DevicesShowcase";
import DonateSection from "Pages/Components/CheckoutV2/DonateSection";
import FooterV2 from "Pages/Components/CheckoutV2/FooterV2";
import "../i18n/config";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { TRIAL_DAYS } from "Pages/Components/CheckoutV2/checkoutV2.constants";

/**
 * Overlay gradient applied on top of the hero background image. The design
 * spec colors (#412468 -> #0C0B11) are used as a semi-transparent overlay so
 * the (intentionally light) photo shows through, instead of an opaque fill
 * that would hide it.
 */

const HERO_OVERLAY =
  "linear-gradient(84.04deg, rgba(65, 36, 104, 0.86) 3.23%, rgba(12, 11, 17, 0.92) 99.39%)";

/** Hero background image, served from `public/DonatePage/`. */
const HeroBg = `${process.env.PUBLIC_URL}/DonatePage/e093a0a254ff68049498037171ed673495d4f8c3.jpg`;
/** Serif display font loaded in `public/index.html`. */
const DISPLAY_SERIF = `"DM Serif Display", Georgia, "Times New Roman", serif`;

interface Benefit {
  /** Rendered SVG element for the benefit tile (includes its own background). */
  icon: React.ReactNode;
  /** i18n key for the tile label. */
  labelKey: string;
  /** i18n key for the text shown beneath the label on hover. */
  descriptionKey: string;
  /** When true, clicking the tile opens the devices showcase in a modal. */
  showDevicesModal?: boolean;
}

/**
 * The first {@link COLLAPSED_BENEFIT_COUNT} benefits are always visible; the
 * rest are revealed when the "See all" control is clicked.
 */
const BENEFITS: Benefit[] = [
  {
    icon: <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="54" height="54" rx="11" fill="#7C6FE0" stroke="white" strokeWidth="2" />
      <path d="M22.75 38.5V15.75L43.75 12.25V35M22.75 38.5C22.75 41.3995 20.3995 43.75 17.5 43.75C14.6005 43.75 12.25 41.3995 12.25 38.5C12.25 35.6005 14.6005 33.25 17.5 33.25C20.3995 33.25 22.75 35.6005 22.75 38.5ZM43.75 35C43.75 37.8995 41.3995 40.25 38.5 40.25C35.6005 40.25 33.25 37.8995 33.25 35C33.25 32.1005 35.6005 29.75 38.5 29.75C41.3995 29.75 43.75 32.1005 43.75 35Z" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    ,
    labelKey: "donationPage.heroAllMusic",
    descriptionKey: "donationPage.heroAllMusicDesc",
  },
  {
    icon: <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="54" height="54" rx="11" fill="#7C6FE0" stroke="white" strokeWidth="2" />
      <path d="M12.25 38.5V28C12.25 23.8228 13.9094 19.8168 16.8631 16.8631C19.8168 13.9094 23.8228 12.25 28 12.25C32.1772 12.25 36.1832 13.9094 39.1369 16.8631C42.0906 19.8168 43.75 23.8228 43.75 28V38.5M43.75 40.25C43.75 41.1783 43.3813 42.0685 42.7249 42.7249C42.0685 43.3813 41.1783 43.75 40.25 43.75H38.5C37.5717 43.75 36.6815 43.3813 36.0251 42.7249C35.3687 42.0685 35 41.1783 35 40.25V35C35 34.0717 35.3687 33.1815 36.0251 32.5251C36.6815 31.8687 37.5717 31.5 38.5 31.5H43.75V40.25ZM12.25 40.25C12.25 41.1783 12.6187 42.0685 13.2751 42.7249C13.9315 43.3813 14.8217 43.75 15.75 43.75H17.5C18.4283 43.75 19.3185 43.3813 19.9749 42.7249C20.6313 42.0685 21 41.1783 21 40.25V35C21 34.0717 20.6313 33.1815 19.9749 32.5251C19.3185 31.8687 18.4283 31.5 17.5 31.5H12.25V40.25Z" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    ,
    labelKey: "donationPage.heroHiRes",
    descriptionKey: "donationPage.heroHiResDesc",
  },
  {
    icon: <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="54" height="54" rx="11" fill="#7C6FE0" stroke="white" strokeWidth="2" />
      <path d="M40.25 15.75V40.25M15.75 14L33.25 28L15.75 42V14Z" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    ,
    labelKey: "donationPage.heroSongSkips",
    descriptionKey: "donationPage.heroSongSkipsDesc",
  },
  {
    icon: <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="54" height="54" rx="11" fill="#7C6FE0" stroke="white" strokeWidth="2" />
      <path d="M28 45.5C37.665 45.5 45.5 37.665 45.5 28C45.5 18.335 37.665 10.5 28 10.5C18.335 10.5 10.5 18.335 10.5 28C10.5 37.665 18.335 45.5 28 45.5Z" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M28 33.25C30.8995 33.25 33.25 30.8995 33.25 28C33.25 25.1005 30.8995 22.75 28 22.75C25.1005 22.75 22.75 25.1005 22.75 28C22.75 30.8995 25.1005 33.25 28 33.25Z" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    ,
    labelKey: "donationPage.heroWhoPlayed",
    descriptionKey: "donationPage.heroWhoPlayedDesc",
  },
  {
    icon: <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="54" height="54" rx="11" fill="#7C6FE0" stroke="white" strokeWidth="2" />
      <path d="M28 27.5C23.8 21.893 19.6 19.1 15.4 19.1C13.1722 19.1 11.0356 19.985 9.4603 21.5603C7.885 23.1356 7 25.2722 7 27.5C7 29.7278 7.885 31.8644 9.4603 33.4397C11.0356 35.015 13.1722 35.9 15.4 35.9C19.6 35.9 23.8 33.107 28 27.5ZM28 27.5C32.2 33.107 36.4 35.9 40.6 35.9C42.8278 35.9 44.9644 35.015 46.5397 33.4397C48.115 31.8644 49 29.7278 49 27.5C49 25.2722 48.115 23.1356 46.5397 21.5603C44.9644 19.985 42.8278 19.1 40.6 19.1C36.4 19.1 32.2 21.893 28 27.5Z" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    ,
    labelKey: "donationPage.heroUnlimited",
    descriptionKey: "donationPage.heroUnlimitedDesc",
  },
  {
    icon: <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="54" height="54" rx="11" fill="#7C6FE0" stroke="white" strokeWidth="2" />
      <g clipPath="url(#clip0_1278_6749)">
        <path d="M28 8.75V47.25M36.75 15.75H23.625C22.0005 15.75 20.4426 16.3953 19.294 17.544C18.1453 18.6926 17.5 20.2505 17.5 21.875C17.5 23.4995 18.1453 25.0574 19.294 26.206C20.4426 27.3547 22.0005 28 23.625 28H32.375C33.9995 28 35.5574 28.6453 36.706 29.794C37.8547 30.9426 38.5 32.5005 38.5 34.125C38.5 35.7495 37.8547 37.3074 36.706 38.456C35.5574 39.6047 33.9995 40.25 32.375 40.25H17.5" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <defs>
        <clipPath id="clip0_1278_6749">
          <rect width="42" height="42" fill="white" transform="translate(7 7)" />
        </clipPath>
      </defs>
    </svg>
    ,
    labelKey: "donationPage.heroTaxDeductible",
    descriptionKey: "donationPage.heroTaxDeductibleDesc",
  },
  {
    icon: <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="54" height="54" rx="11" fill="#7C6FE0" stroke="white" strokeWidth="2" />
      <path d="M42 28V45.5H14V28M28 45.5V19.25M28 19.25H20.125C18.9647 19.25 17.8519 18.7891 17.0314 17.9686C16.2109 17.1481 15.75 16.0353 15.75 14.875C15.75 13.7147 16.2109 12.6019 17.0314 11.7814C17.8519 10.9609 18.9647 10.5 20.125 10.5C26.25 10.5 28 19.25 28 19.25ZM28 19.25H35.875C37.0353 19.25 38.1481 18.7891 38.9686 17.9686C39.7891 17.1481 40.25 16.0353 40.25 14.875C40.25 13.7147 39.7891 12.6019 38.9686 11.7814C38.1481 10.9609 37.0353 10.5 35.875 10.5C29.75 10.5 28 19.25 28 19.25ZM10.5 19.25H45.5V28H10.5V19.25Z" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    ,
    labelKey: "donationPage.heroSwag",
    descriptionKey: "donationPage.heroSwagDesc",
  },
  {
    icon: <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="54" height="54" rx="11" fill="#7C6FE0" stroke="white" strokeWidth="2" />
      <path d="M28 17.5H28.0175M17.5 10.5H38.5C40.433 10.5 42 12.067 42 14V42C42 43.933 40.433 45.5 38.5 45.5H17.5C15.567 45.5 14 43.933 14 42V14C14 12.067 15.567 10.5 17.5 10.5ZM35 31.5C35 35.366 31.866 38.5 28 38.5C24.134 38.5 21 35.366 21 31.5C21 27.634 24.134 24.5 28 24.5C31.866 24.5 35 27.634 35 31.5Z" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    ,
    labelKey: "donationPage.heroDevices",
    descriptionKey: "donationPage.heroDevicesDesc",
    showDevicesModal: true,
  },
];

const COLLAPSED_BENEFIT_COUNT = 3;

interface BenefitTileProps {
  benefit: Benefit;
}

const BenefitTile = ({ benefit }: BenefitTileProps) => {
  const { t } = useTranslation();
  const { icon, labelKey, descriptionKey, showDevicesModal } = benefit;
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <Box
      onClick={showDevicesModal ? () => setModalOpen(true) : undefined}
      sx={{
        // Mobile: fill its (1/3-width) cell so the row packs 3 across.
        // Desktop: fixed 126px (the horizontal reveal needs a rigid width).
        width: { xs: "100%", lg: 126 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        color: "common.white",
        cursor: showDevicesModal ? "pointer" : "default",
        // On hover, lift the icon and swap its colors: the purple fill
        // (#7C6FE0) becomes white and the white strokes become #7C6FE0.
        
        [`&:hover .jg-benefit-tile__icon svg [fill="#7C6FE0"]`]: {
          fill: "#fff",
        },
        [`&:hover .jg-benefit-tile__icon svg [stroke="white"]`]: {
          stroke: "#7C6FE0",
        },
        [`&:hover .jg-benefit-tile__description`]: {
          display: "block",
        },
      }}
    >
      <Box
        className="jg-benefit-tile__icon"
        sx={(theme) => ({
          width: 56,
          height: 56,
          lineHeight: 0,
          transition: theme.transitions.create("transform"),
          // Smooth color transition for the swap on hover.
          "& svg [fill], & svg [stroke]": {
            transition: theme.transitions.create(["fill", "stroke"]),
          },
          "& svg": { display: "block", width: 56, height: 56 },
        })}
      >
        {icon}
      </Box>

      {/* Small underline accent shown beneath every tile. */}
      <Box
        sx={{
          width: 22,
          height: 2,
          borderRadius: 1,
          backgroundColor: "common.white",
          mt: 1.25,
          mb: 1,
        }}
      />

      <Typography
        sx={{
          fontFamily: `"DM Sans", sans-serif`,
          fontSize: "14px",
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        {t(labelKey)}
      </Typography>

      <Typography
        className="jg-benefit-tile__description"
        variant="caption"
        sx={{
          mt: 0.75,
          maxWidth: 130,
          fontFamily: `"DM Sans", sans-serif`,
          display: "none",
          color: alpha("#FFFFFF", 0.85),
          transition: (theme) => theme.transitions.create("opacity"),
        }}
      >
        {t(descriptionKey)}
      </Typography>

      {showDevicesModal && (
        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          aria-label={t(labelKey)}
          // Modal renders in a portal, but React replays events through the
          // component tree — without this, clicks inside bubble back to the
          // tile's onClick and immediately reopen the modal.
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Box
            sx={{
              position: "relative",
              outline: "none",
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
          >
            <ButtonBase
              onClick={() => setModalOpen(false)}
              aria-label={t("donationPage.close")}
              sx={{
                position: "absolute",
                top: 2,
                right: 7,
                width: 36,
                height: 36,
                // borderRadius: "50%",
                // backgroundColor: "#7C6FE0",
                color: "common.white",
                zIndex: 99,
              }}
            >
              <JGIcon is={IoClose} sx={{ fontSize: 24 }} />
            </ButtonBase>
            {/* The devices showcase replaces the previous static screenshot so
                the modal stays in sync with the Free Trial page. It keeps the
                same footprint the <img> had (651px wide, capped at 90vh).
                Page-level spacing (fixed height + top margin) is dropped. */}
            <DevicesShowcase
              sx={{
                height: "auto",
                margin: 0,
                maxHeight: "95vh",
                overflow: "auto",
              }}
              onTryFree={() => setModalOpen(false)}
            />
          </Box>
        </Modal>
      )}
    </Box>
  );
};

interface HeroBenefitsProps {
  expanded: boolean;
  onToggle: () => void;
}

/** Animated horizontal spacing between benefit tiles. */
const BENEFIT_GAP = {xs:8, lg: 16};

/**
 * Shared timing for the benefits reveal so every animated property (size,
 * slide, fade) moves in lockstep on one smooth curve.
 */
const REVEAL_DURATION = ".50s";
const REVEAL_EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

const HeroBenefits = ({ expanded, onToggle }: HeroBenefitsProps) => {
  const { t } = useTranslation();
  const hiddenCount = BENEFITS.length - COLLAPSED_BENEFIT_COUNT;

  // Per-tile footprint + reveal. All tiles share one wrap container.
  //  - Mobile: each tile is 1/3 of the row so they pack 3-per-row (3 / 3 / 2);
  //    hidden tiles collapse their HEIGHT (reveal bottom<->top).
  //  - Desktop: each tile is a rigid 126px; hidden tiles collapse their WIDTH
  //    (reveal right<->left). flexShrink:0 keeps the tile rigid so the
  //    width-collapse only ever CLIPS it — it never re-wraps/squishes text.
  const third = `calc((100% - ${3 * BENEFIT_GAP.xs}px) / 3)`;
  const tileSx = (shown: boolean) => ({
    flexGrow: 0,
    flexShrink: { xs: 1, lg: 0 },
    minWidth: 0,
    overflow: "hidden",
    flexBasis: { xs: third, lg: "auto" },
    maxWidth: { xs: third, lg: shown ? "126px" : "0px" },
    maxHeight: { xs: shown ? "260px" : "0px", lg: "260px" },
    opacity: shown ? 1 : 0,
    transform: {
      xs: shown ? "translateY(0)" : "translateY(12px)",
      lg: shown ? "translateX(0)" : "translateX(20px)",
    },
    // Horizontal gap: always present on mobile; on desktop it collapses with
    // the tile so hidden tiles take zero room in the row.
    marginLeft: { xs: `${BENEFIT_GAP.xs / 2}px`, lg: shown ? `${BENEFIT_GAP.lg / 2}px` : 0 },
    marginRight: { xs: `${BENEFIT_GAP.xs / 2}px`, lg: shown ? `${BENEFIT_GAP.lg / 2}px` : 0 },
    // Vertical gap between mobile rows; collapses for hidden tiles (we avoid
    // rowGap, which would leave phantom gaps around the zero-height rows).
    marginBottom: { xs: shown ? `${BENEFIT_GAP.lg}px` : 0, lg: 0 },
    transition: [
      `max-width ${REVEAL_DURATION} ${REVEAL_EASE}`,
      `max-height ${REVEAL_DURATION} ${REVEAL_EASE}`,
      `opacity ${REVEAL_DURATION} ${REVEAL_EASE}`,
      `transform ${REVEAL_DURATION} ${REVEAL_EASE}`,
      `margin ${REVEAL_DURATION} ${REVEAL_EASE}`,
    ].join(", "),
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: { xs: "wrap", lg: "nowrap" },
        justifyContent: "center",
        alignItems: "flex-start",
        maxWidth: { xs: "394px", lg: "100%" },
        margin: "0 auto",
      }}
    >
      {/* All tiles live in one container; the extra ones animate open/closed. */}
      {BENEFITS.map((benefit, index) => {
        const shown = expanded || index < COLLAPSED_BENEFIT_COUNT;
        return (
          <Box key={benefit.labelKey} sx={tileSx(shown)}>
            <BenefitTile benefit={benefit} />
          </Box>
        );
      })}

      <ButtonBase
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={expanded ? t("donationPage.showFewerBenefits") : t("donationPage.seeAllBenefits", { count: hiddenCount })}
        sx={{
          flexShrink: 0,
          // Mobile: take a full-width row so the toggle sits centered on its
          // own line (below the tiles when collapsed, below the grid when
          // expanded). Desktop: stay inline at the end of the no-wrap row, so
          // flex reflow slides it rightward as the hidden tiles open.
          flexBasis: { xs: "100%", lg: "auto" },
          width: { xs: "auto", lg: 53 },
          height: {xs:50, lg: 90},
          mx: { xs: `${BENEFIT_GAP.xs / 2}px`, lg: `${BENEFIT_GAP.lg / 2}px` },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "common.white",
          borderRadius: 2,
          transition: "transform .45s ease, color .2s ease",
          // Fade the +/arrow content in whenever the toggle morphs, so the
          // swap between states is smooth rather than an instant cut.
          "@keyframes jgToggleFade": {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        }}
      >
        {expanded ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              animation: "jgToggleFade .3s ease",
            }}
          >
            {/* Desktop collapse arrow points left; mobile points up. */}
            <JGIcon
              is={IoChevronBack}
              sx={{ fontSize: 30, color: "common.white", display: { xs: "none", lg: "block" } }}
            />
            <Typography
              variant="caption"
              sx={{ mt: 1, fontWeight: 600, maxWidth: 90, lineHeight: 1.2,display: { xs: "none", lg: "block" } }}
            >
              {t("donationPage.seeLess")}
            </Typography>
            {/* <Box sx={{ display: { xs: "none", lg: "block" } }}>
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.4001 16L20.5334 22.1333L18.6667 24L10.6667 16L18.6667 8L20.5334 9.86667L14.4001 16Z" fill="white"/>
              </svg>

            </Box> */}
            <JGIcon
              is={IoChevronUp}
              sx={{ fontSize: 30, color: "common.white", display: { xs: "block", lg: "none" } }}
            />
            <Typography
              variant="caption"
              sx={{ mt: 1, fontWeight: 600, maxWidth: 90, lineHeight: 1.2,display: { xs: "block", lg: "none" } }}
            >
              {t("donationPage.seeLess")}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              animation: "jgToggleFade .3s ease",
              mt:{ xs: 4, lg: 0}
            }}
          >
            {/* <JGIcon is={IoAdd} sx={{ fontSize: 30, color: "common.white" }} /> */}
            <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M23.0007 11.3335V34.6668M11.334 23.0002H34.6673" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>

            <Typography
              variant="caption"
              sx={{ mt: 0.5, fontWeight: 600, maxWidth: 90, lineHeight: 1.2 }}
            >
              {t("donationPage.seeAllBenefits", { count: hiddenCount })}
            </Typography>
          </Box>
        )}
      </ButtonBase>
    </Box>
  );
};

export const DonateHero = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // Same rule as DonateSection: a ?coupon= link is a free-trial signup.
  const trialMode = !!new URLSearchParams(useLocation().search)
    .get("coupon")
    ?.trim();

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: { xs: 558, lg: 650 },
        height: { xs: "100%", lg: 650 },
        background: `${HERO_OVERLAY}, url('${HeroBg}')`,
        backgroundSize: "cover",
        backgroundPosition: "top",
        backgroundRepeat: "no-repeat",
        overflow: "hidden",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          pt: { xs: 12, md: 16 },
        }}
      >
        <Box
          sx={{
            display: "inline-block",
            backgroundColor: "#7C6FE0",
            border: "1px solid #8B7FEA",
            borderRadius: "8px",
            py: 0.75,
            px: {xs: "6px", md: "16px"},
            mb: {xs: "33px", md: "20px"},
          }}
        >
          <Typography
            sx={{
              color: "common.white",
              fontFamily: `"Inter", sans-serif`,
              fontSize: {xs:"14px",md: "16px"},
              fontWeight: 600,
              lineHeight: 1,
              letterSpacing: "2.5px",
            }}
          >
            {t("donationPage.heroNonprofit")}
          </Typography>
        </Box>

        <Typography
          component="h1"
          sx={{
            fontFamily: DISPLAY_SERIF,
            fontWeight: 400,
            color: "common.white",
            lineHeight: 1.08,
            maxWidth: { xs: "20ch", md: 800 },
            fontSize: { xs: "2rem", md: "52px" },
          }}
        >
          {t(trialMode ? "donationPage.trialHeroTagline" : "donationPage.heroTagline")}
        </Typography>

        <Typography
          sx={{
            color: "common.white",
            fontFamily: `"DM Sans", sans-serif`,
            fontSize: "16px", 
            fontWeight: 500,
            lineHeight: "20px",
            mt: "33px",
            maxWidth: 350,
            display: { xs: "block", md: "none" }
          }}
        >
          {trialMode
            ? t("donationPage.trialHeroBenefitsNote", { days: TRIAL_DAYS })
            : t("donationPage.heroBenefitsNote")}
        </Typography>

        <Box
          sx={{
            width: {xs: "min(200px, 100%)", md: "min(560px, 70%)"},
            height: "1px",
            backgroundColor: "#ffffff",
            borderRadius: "1px",
            margin: {xs: "33px 33px", md: "44px 48px"},
          }}
        />

        <Typography
          sx={{
            color: "common.white",
            fontFamily: `"DM Sans", sans-serif`,
            fontSize: "20px",
            fontWeight: 500,
            lineHeight: 1.5,
            mb: "48px",
            display: { xs: "none", md: "block" }
          }}
        >
          {trialMode
            ? t("donationPage.trialHeroBenefitsNote", { days: TRIAL_DAYS })
            : t("donationPage.heroBenefitsNote")}
        </Typography>
      </Container>

      {/* Full-width so the expanded benefits fit on a single row. */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          px: 2,
          pb: { xs: 8, md: 12.5 },
        }}
      >
        <HeroBenefits
          expanded={expanded}
          onToggle={() => setExpanded((prev) => !prev)}
        />
      </Box>
    </Box>
  );
};

const CheckoutV2 = () => {
  const { t } = useTranslation();

  return (
    <>
      <Title value={t("donationPage.title")} />

      <DefaultLayout
        disablePlayer
        disableFooter
        disableBanner
        disableNavItems
        disableDonateButton
        donationPageV2
      >
        <DonateHero />
        <DonateSection />
        <FooterV2 />
      </DefaultLayout>
    </>
  );
};

export default CheckoutV2;
